const OUTCOME_TIMEOUT_MS = 20000
const SETTLED_AFTER_MS = 1500
const POLL_MS = 100
const HIDDEN_CONTENT = '[id^="test-code-"], .emergency-codes-container, .qr-code-container, .modal-backdrop, #loadingScreen'
const SECRET_CONTENT = '[id^="test-code-"], .emergency-codes-container, .qr-code-container, #qrCodeNotWorkingModal, #loadingScreen'
const SECRET_INPUTS = 'input[type="password"]:not(#ssn):not(#ptin), input[autocomplete="current-password"], input[autocomplete="new-password"], input[autocomplete="one-time-code"], #twoFactorCode'
const MAX_DIALOG_TEXT = 800
const MAX_ACTIONS = 40
const PERSON_ONLY = '[data-testid="confirm-link-submit"]'
const REVEAL_TOGGLE = /password visibility|show password|hide password/i
const TERMS_CLAUSE = /By clicking\s+(.+?),\s+you agree/i
const INFO_DIALOGS = /^(terms|privacy|security|accessibility|faq\d[\d-]*|helpTwoStep|recoveringYourAccount|recoveringPreparerAccount|qrCodeNotWorking|externalBrowser)Modal$/

const mainArea = () => document.getElementById('mainContentArea') ?? document.body

export const findElement = (selector, description) => {
	const element = document.querySelector(selector)
	if (!element) {
		throw new Error(`This page has no ${description}. Call get_auth_state to see which step the person is on.`)
	}
	return element
}

export const isVisible = (element) => {
	if (!element?.isConnected) return false
	if (element.closest('.d-none, .modal-backdrop-hidden, [hidden]')) return false
	const style = getComputedStyle(element)
	return style.display !== 'none' && style.visibility !== 'hidden'
}

export const cleanText = (element) => (element?.textContent ?? '').replace(/\s+/g, ' ').trim()

export const isSecretInput = (element) => element.matches(SECRET_INPUTS)

export const labelFor = (element) => cleanText(element.labels?.[0] ?? element.closest('label')) || element.getAttribute('aria-label') || element.name || element.id

export const fillInput = (selector, value, description) => {
	const element = findElement(selector, description)
	element.focus()
	element.value = String(value ?? '')
	element.dispatchEvent(new Event('input', {bubbles: true}))
	element.dispatchEvent(new Event('change', {bubbles: true}))
	element.blur()
	return element
}

export const setChecked = (selector, checked, description) => {
	const element = findElement(selector, description)
	if (element.checked !== Boolean(checked)) element.click()
	return element
}

export const chooseOption = (selector, description) => setChecked(selector, true, description)

export const formatPhoneForInput = (value) => {
	const digits = String(value ?? '').replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '')
	if (digits.length !== 10) {
		throw new Error('Phone numbers need 10 digits, for example (801) 555-1234.')
	}
	return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

export const formatSsnForInput = (value) => {
	const digits = String(value ?? '').replace(/\D/g, '')
	if (digits.length !== 9) {
		throw new Error('A Social Security number has 9 digits.')
	}
	return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`
}

const invalidFields = (form) => [...form.elements]
	.filter((element) => element.willValidate && !element.checkValidity())
	.map((element) => ({field: labelFor(element), problem: element.validationMessage || 'This field is required.'}))

export const press = (selector, description) => pressElement(findElement(selector, description), description)

export const pressElement = (button, description) => {
	if (!isVisible(button)) {
		throw new Error(`The ${description} is not showing on this page right now.`)
	}
	if (button.disabled) {
		throw new Error(`The ${description} is disabled, usually because a request is already in progress. Wait, then call get_auth_state.`)
	}
	const form = button.type === 'submit' ? button.form : null
	if (form) {
		const invalid = form.noValidate ? [] : invalidFields(form)
		if (invalid.length) return {invalid}
		form.requestSubmit(button)
	} else {
		button.click()
	}
	return {invalid: []}
}

export const findButtonByText = (text, root = mainArea()) => [...root.querySelectorAll('button, a')]
	.find((element) => isVisible(element) && cleanText(element).toLowerCase() === text.toLowerCase())

export const pressButtonWithText = (text) => {
	const button = findButtonByText(text) ?? findButtonByText(text, document.body)
	if (!button) {
		throw new Error(`There is no "${text}" button on this page. Call get_auth_state to see the buttons that are showing.`)
	}
	if (button.disabled) {
		throw new Error(`The "${text}" button is disabled right now.`)
	}
	button.click()
}

export const isLoading = () => {
	const loadingScreen = document.getElementById('loadingScreen')
	return Boolean(loadingScreen && !loadingScreen.classList.contains('d-none'))
}

export const readAlerts = () => [...document.querySelectorAll('#toastAlertArea .alert-message')].map((alert) => ({
	type: ['error', 'warning', 'info', 'success'].find((type) => alert.classList.contains(`${type}-message`)) ?? 'info',
	title: cleanText(alert.querySelector('.toast-alert-content > span.d-block')) || undefined,
	message: cleanText(alert.querySelector('.toast-alert-content > span:not(.d-block)')),
	field: alert.dataset.field || undefined,
}))

export const openDialog = () => [...document.querySelectorAll('.modal-backdrop:not(.modal-backdrop-hidden):not(.side-drawer)')]
	.find((modal) => modal.querySelector('[role="dialog"]') && getComputedStyle(modal).display !== 'none')

const accessibleName = (element) => cleanText(element) || element.getAttribute('aria-label') || element.title || ''

const isRevealToggle = (element) => element.matches('.hidden-input')
	|| REVEAL_TOGGLE.test(accessibleName(element))
	|| Boolean(element.type === 'button' && element.parentElement?.matches('.position-relative') && [...element.parentElement.querySelectorAll(':scope > input')].some(isSecretInput))

const termsLabels = () => [...document.querySelectorAll('p')]
	.filter(isVisible)
	.map((paragraph) => cleanText(paragraph).match(TERMS_CLAUSE)?.[1]?.toLowerCase())
	.filter(Boolean)

const acceptsTerms = (element, labels = termsLabels()) => {
	const name = accessibleName(element).toLowerCase()
	return Boolean(name) && labels.some((label) => name.startsWith(label) || label.startsWith(name))
}

export const isPersonOnly = (element, labels = termsLabels()) => element.matches(PERSON_ONLY) || isRevealToggle(element) || acceptsTerms(element, labels)

export const personOnlyButtons = (root = mainArea()) => {
	const labels = termsLabels()
	const scope = openDialog() ?? root
	return [...scope.querySelectorAll('button, a[href]')]
		.filter((element) => isVisible(element) && !isRevealToggle(element) && isPersonOnly(element, labels))
		.map(accessibleName)
		.filter((name, index, all) => name && all.indexOf(name) === index)
}

const isDisplayAction = (element, dialog) => {
	if (dialog && INFO_DIALOGS.test(dialog.id)) return true
	if (element.matches('.btn-close, [aria-label^="Close" i]')) return true
	return Boolean(element.closest('p') && /you agree to our/i.test(cleanText(element.closest('p'))))
}

const isActionCandidate = (element) => isVisible(element)
	&& !element.closest(`${SECRET_CONTENT}, #toastAlertArea`)
	&& !element.matches('.help-a, [target="_blank"], a[href^="mailto:"], a[href^="tel:"]')
	&& accessibleName(element)

export const actionTargets = () => {
	const dialog = openDialog()
	const scope = dialog ?? mainArea()
	const labels = termsLabels()
	const elements = [...scope.querySelectorAll('button, a[href], [role="button"]')]
		.filter((element) => dialog || !element.closest('.modal-backdrop'))
		.filter((element) => isActionCandidate(element) && !isPersonOnly(element, labels))
	const seen = {}
	return elements.slice(0, MAX_ACTIONS).map((element) => {
		const name = accessibleName(element).slice(0, 80)
		seen[name] = (seen[name] ?? 0) + 1
		return {label: seen[name] > 1 ? `${name} (${seen[name]})` : name, element, display: isDisplayAction(element, dialog)}
	})
}

const readDialog = () => {
	const dialog = openDialog()
	if (!dialog) return undefined
	const text = [...dialog.querySelectorAll('.modal-body p, .modal-body li')]
		.filter((element) => isVisible(element) && !element.closest(SECRET_CONTENT))
		.map(cleanText)
		.filter(Boolean)
		.join(' ')
	return {
		heading: cleanText(dialog.querySelector('h1, h2, h3')),
		text: text.length > MAX_DIALOG_TEXT ? `${text.slice(0, MAX_DIALOG_TEXT)}…` : text,
		buttons: actionTargets().map(({label}) => label),
	}
}

export const dialogKey = () => {
	const dialog = openDialog()
	return dialog ? dialog.id || cleanText(dialog.querySelector('h1, h2, h3')) : ''
}

export const readFieldErrors = () => [...mainArea().querySelectorAll('.input-error-message')]
	.filter(isVisible)
	.map((error) => cleanText(error))
	.filter(Boolean)

export const isCaptchaChallengeShowing = () => [...document.querySelectorAll('iframe[src*="hcaptcha"]')]
	.some((frame) => /challenge/i.test(frame.title ?? '') && isVisible(frame.parentElement ?? frame) && frame.getBoundingClientRect().width > 0)

const readFields = (root) => {
	const fields = []
	const groups = new Map()
	root.querySelectorAll('input, select, textarea').forEach((element) => {
		if (['hidden', 'submit', 'button'].includes(element.type) || element.closest(HIDDEN_CONTENT)) return
		if (!isVisible(element) && !(element.type === 'radio' && isVisible(element.labels?.[0]))) return
		if (element.type === 'radio') {
			const key = element.name || element.id
			if (!groups.has(key)) {
				const legend = cleanText(element.closest('fieldset')?.querySelector('legend'))
				const group = {name: key, type: 'choice', label: legend || key, options: []}
				groups.set(key, group)
				fields.push(group)
			}
			groups.get(key).options.push({value: element.value, label: labelFor(element), selected: element.checked})
			return
		}
		fields.push({
			id: element.id || undefined,
			type: element.type === 'checkbox' ? 'checkbox' : element.tagName === 'SELECT' ? 'select' : element.type,
			label: labelFor(element),
			required: element.required || undefined,
			maxLength: element.maxLength > 0 ? element.maxLength : undefined,
			options: element.tagName === 'SELECT' ? [...element.options].map((option) => cleanText(option)).filter(Boolean) : undefined,
			checked: element.type === 'checkbox' ? element.checked : undefined,
			filled: ['checkbox', 'select-one'].includes(element.type) ? undefined : element.value !== '',
			secret: isSecretInput(element) || undefined,
		})
	})
	return fields
}

const readButtons = (root) => [...root.querySelectorAll('button, a.btn, a[href="#"]')]
	.filter((element) => isVisible(element) && !element.closest(HIDDEN_CONTENT) && !isRevealToggle(element) && cleanText(element))
	.map((element) => cleanText(element))
	.filter((text, index, all) => all.indexOf(text) === index)

const readInstructions = (root) => {
	const text = [...root.querySelectorAll('p, li, h2, h3')]
		.filter((element) => isVisible(element) && !element.closest(`${HIDDEN_CONTENT}, #toastAlertArea, form`))
		.map(cleanText)
		.filter(Boolean)
		.join(' ')
	return text.length > 1200 ? `${text.slice(0, 1200)}…` : text
}

export const readPage = () => {
	const root = mainArea()
	return {
		heading: cleanText(root.querySelector('h1')),
		instructions: readInstructions(root),
		fields: readFields(root),
		buttons: readButtons(root),
		personPresses: personOnlyButtons(root),
		dialog: readDialog(),
		alerts: readAlerts(),
		fieldErrors: readFieldErrors(),
		loading: isLoading(),
		captchaChallengeShowing: isCaptchaChallengeShowing(),
	}
}

const alertsKey = () => JSON.stringify(readAlerts())

export const snapshotPage = () => ({path: location.pathname, dialog: dialogKey(), alerts: alertsKey(), errors: readFieldErrors().join('|')})

const alertOutcome = (alerts, describeStep) => {
	const invalid = alerts.filter((alert) => alert.field && ['error', 'warning'].includes(alert.type))
	if (invalid.length) return {outcome: 'invalid_fields', invalid: invalid.map((alert) => ({field: alert.field, problem: alert.message})), alerts, step: describeStep?.()}
	return {outcome: 'message_shown', alerts, step: describeStep?.()}
}

export const waitForOutcome = ({describeStep, timeoutMs = OUTCOME_TIMEOUT_MS, start = snapshotPage(), startPath = start.path, isSettled = () => true, signal} = {}) => new Promise((resolve) => {
	if (signal?.aborted) return resolve({outcome: 'cancelled', step: describeStep?.()})
	let alertsChanged = alertsKey() !== start.alerts
	const observer = new MutationObserver((mutations) => {
		if (mutations.some((mutation) => mutation.target.closest?.('#toastAlertArea'))) alertsChanged = true
	})
	observer.observe(document.body, {childList: true, subtree: true})
	const startErrors = start.errors
	const startDialog = start.dialog
	const startedAt = Date.now()
	let lastBusyAt = startedAt
	let timer

	const finish = (result) => {
		clearInterval(timer)
		observer.disconnect()
		window.removeEventListener('beforeunload', onLeave)
		window.navigation?.removeEventListener?.('navigate', onNavigate)
		signal?.removeEventListener('abort', onAbort)
		resolve(result)
	}
	const onAbort = () => finish({outcome: 'cancelled', step: describeStep?.()})
	const onLeave = () => finish({
		outcome: 'leaving_page',
		note: 'The browser is leaving the sign-in site, usually for the tax return. Wait for the next page to load, then read it.',
	})
	const onNavigate = (event) => {
		if (event.destination && !event.destination.sameDocument) onLeave()
	}

	window.addEventListener('beforeunload', onLeave)
	window.navigation?.addEventListener?.('navigate', onNavigate)
	signal?.addEventListener('abort', onAbort)

	timer = setInterval(() => {
		const now = Date.now()
		if (isLoading()) {
			lastBusyAt = now
			if (now - startedAt > timeoutMs) finish({outcome: 'still_loading', step: describeStep?.()})
			return
		}
		if (isCaptchaChallengeShowing()) {
			return finish({
				outcome: 'waiting_for_person',
				waitingFor: 'captcha',
				reason: 'A human check (captcha) is showing. Only the person can solve it. Ask them to complete it, then call get_auth_state.',
			})
		}
		if (location.pathname !== startPath) {
			if (!isSettled() && now - startedAt <= timeoutMs) return
			return finish({outcome: 'next_step', step: describeStep?.()})
		}
		if (alertsChanged && readAlerts().length) {
			return finish(alertOutcome(readAlerts(), describeStep))
		}
		if (readFieldErrors().join('|') !== startErrors && readFieldErrors().length) {
			return finish({outcome: 'field_errors', fieldErrors: readFieldErrors(), step: describeStep?.()})
		}
		if (dialogKey() !== startDialog) {
			if (!isSettled() && now - startedAt <= timeoutMs) return
			return finish({outcome: dialogKey() ? 'dialog_opened' : 'dialog_closed', step: describeStep?.()})
		}
		if (now - lastBusyAt > SETTLED_AFTER_MS) {
			finish({outcome: 'no_change', step: describeStep?.()})
		}
	}, POLL_MS)
})
export const waitForElement = (selector, timeoutMs = 8000) => new Promise((resolve) => {
	const startedAt = Date.now()
	const timer = setInterval(() => {
		const element = document.querySelector(selector)
		if ((element && isVisible(element) && !isLoading()) || Date.now() - startedAt > timeoutMs) {
			clearInterval(timer)
			resolve(element && isVisible(element) ? element : null)
		}
	}, POLL_MS)
})